import React, { useState, useRef, useCallback, useEffect } from 'react'
import { css, keyframes } from '@emotion/core'
import { number, string, func, bool, shape } from 'prop-types'
import { H1, Paragraph, MarginBox } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useFeedBadge } from '../../providers/FeedBadgeProvider'
import useIsComponentMounted from '../../hooks/useIsComponentMounted'
import useCancelableWork from '../../hooks/useCancelableWork'
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion'
import PointsAnimator from '../PointsAnimator'
import StoryThumbnailCard from '../StoryThumbnailCard'
import { Z_INDICES } from '../../constants/zIndices'

const TIMING = {
  COUNT_UP: 3, // seconds
  POST_COUNT_DELAY: 700, // ms
  CARD_ANIMATION_DELAY: 700, // ms
  FADE_OUT: 0.5, // seconds
  FADE_IN: 0.3, // seconds
  SLIDE_UP: 0.5, // seconds
}

// Z-index layers for animation stacking
const Z_INDEX = Z_INDICES.FLY_ANIMATION

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`

const slideUpFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(300px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const unblur = keyframes`
  from {
    filter: blur(10px);
    opacity: 0.3;
  }
  to {
    filter: blur(0);
    opacity: 1;
  }
`

const fullScreenCss = css`
  position: fixed;
  inset: 0;
`

const overlayCss = css`
  ${fullScreenCss};
  z-index: ${Z_INDEX.OVERLAY};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: none;
`

const getBackdropCss = (isFading) => css`
  ${fullScreenCss};
  background-color: #ffffff;
  z-index: ${Z_INDEX.BACKDROP};
  animation: ${isFading ? fadeOut : fadeIn} ${isFading ? TIMING.FADE_OUT : TIMING.FADE_IN}s ease-out forwards;
`

const cardBaseCss = css`
  position: fixed;
  will-change: transform, opacity;
`

const getCardPhaseCss = (flyDurationSeconds) => ({
  counting: css`
    ${cardBaseCss};
    z-index: ${Z_INDEX.CARD_BACKGROUND};
    filter: blur(10px);
    opacity: 0.3;
    transition:
      filter 0.5s ease-out,
      opacity 0.5s ease-out;
  `,
  revealing: css`
    ${cardBaseCss};
    z-index: ${Z_INDEX.CARD_FOREGROUND};
    animation: ${unblur} 0.5s ease-out forwards;
  `,
  flying: css`
    ${cardBaseCss};
    z-index: ${Z_INDEX.CARD_FOREGROUND};
    transition:
      transform ${flyDurationSeconds}s cubic-bezier(0.34, 1.56, 0.64, 1),
      opacity ${flyDurationSeconds}s cubic-bezier(0.34, 1.56, 0.64, 1);
  `,
})

const getPointsContainerCss = (isFading) => css`
  z-index: ${Z_INDEX.OVERLAY};
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${isFading ? fadeOut : slideUpFadeIn} ${isFading ? 0.4 : TIMING.SLIDE_UP}s ease-out forwards;
`

/**
 * FlyToFeedAnimation - A component that overlays the UI with a blurred background,
 * displays points animation with the StoryThumbnailCard blurred behind it,
 * then reveals the card and animates it to fly to the feed icon.
 *
 * Animation phases:
 * 1. Points counting with card blurred in background
 * 2. Points fade out, card unblurs and is revealed
 * 3. Card flies to feed icon position
 */
export default function FlyToFeedAnimation({
  duration = 1500,
  onAnimationComplete,
  startPoints = 0,
  endPoints = 0,
  isSharedToGroup = false,
  headerImage,
  imageWidth = 200,
  imageHeight = 150,
  title,
  firstAnswerBody,
  cardPosition,
}) {
  const { t } = useTranslation()
  const { incrementBadgeCount, feedIconRef } = useFeedBadge()
  const prefersReducedMotion = usePrefersReducedMotion()
  // Animation phases: 'counting' -> 'revealing' -> 'flying' -> 'done'
  const [phase, setPhase] = useState('counting')
  const cardRef = useRef(null)

  const isMountedRef = useIsComponentMounted()
  const scheduledWork = useCancelableWork()

  const flyCompletionHandledRef = useRef(false)
  // Ensures the reduced-motion effect runs at most once (even if deps change).
  const reducedMotionHandledRef = useRef(false)

  const [cardStyle, setCardStyle] = useState(() =>
    cardPosition
      ? {
          top: cardPosition.top,
          left: cardPosition.left,
          width: cardPosition.width,
          // Keep the card's measured size stable to avoid layout shifts during transitions.
          height: cardPosition.height,
        }
      : {
          top: '30%',
          left: '50%',
          transform: 'translateX(-50%)',
        }
  )

  const subtextMessage = isSharedToGroup
    ? t('finish.ready-to-go', 'Your latest story is ready to go. It will be posted in the Family Feed.')
    : ''

  const startFlyAnimation = useCallback(() => {
    if (!cardRef.current) return

    // Cancel any pending work from prior phases before starting the fly.
    scheduledWork.clearAll()

    flyCompletionHandledRef.current = false

    const completeFly = () => {
      if (!isMountedRef.current) return
      if (flyCompletionHandledRef.current) return
      flyCompletionHandledRef.current = true
      setPhase('done')
      if (isSharedToGroup) incrementBadgeCount()
      onAnimationComplete?.()
    }

    const cardEl = cardRef.current
    let cleanupTransitionEnd = () => {}
    const handleTransitionEnd = (event) => {
      if (event.target !== cardEl) return
      // We transition both transform and opacity; pick one as the authoritative finish.
      if (event.propertyName && event.propertyName !== 'transform') return
      cleanupTransitionEnd()
      completeFly()
    }

    cardEl.addEventListener('transitionend', handleTransitionEnd)
    cleanupTransitionEnd = scheduledWork.registerCleanup(() => {
      cardEl.removeEventListener('transitionend', handleTransitionEnd)
    })

    let targetX = window.innerWidth / 2
    let targetY = window.innerHeight - 40

    if (feedIconRef) {
      const rect = feedIconRef.getBoundingClientRect()
      targetX = rect.left + rect.width / 2
      targetY = rect.top + rect.height / 2
    }

    const cardRect = cardRef.current.getBoundingClientRect()
    const cardCenterX = cardRect.left + cardRect.width / 2
    const cardCenterY = cardRect.top + cardRect.height / 2

    const translateX = targetX - cardCenterX
    const translateY = targetY - cardCenterY

    // First, set phase to flying (applies transition CSS)
    if (!isMountedRef.current) return
    setPhase('flying')

    // Check if transitions are disabled and complete immediately if so
    const checkTransitionDisabled = () => {
      if (!isMountedRef.current) return
      const transitionDuration = window.getComputedStyle(cardEl).transitionDuration
      const firstDuration = String(transitionDuration || '')
        .split(',')[0]
        ?.trim()
      if (!firstDuration || firstDuration === '0s' || firstDuration === '0ms') {
        completeFly()
      }
    }

    // Apply the fly transform after CSS transition is set
    const applyFlyTransform = () => {
      if (!isMountedRef.current) return
      // Preserve any existing base transform (e.g. translateX(-50%)) while adding fly transform.
      setCardStyle((prev) => {
        const baseTransform = prev?.transform ? String(prev.transform) : ''
        const basePrefix = baseTransform ? `${baseTransform} ` : ''
        return {
          ...prev,
          transform: `${basePrefix}translate(${translateX}px, ${translateY}px) scale(0.1)`,
          opacity: 0,
        }
      })

      // If transitions are disabled (duration 0), transitionend may not fire.
      // In that case, we complete on the next frame to avoid hanging.
      scheduledWork.scheduleRaf(checkTransitionDisabled)
    }

    // Wait for next frame so the transition CSS is applied before we change the transform
    scheduledWork.scheduleRaf(() => {
      scheduledWork.scheduleRaf(applyFlyTransform)
    })
  }, [feedIconRef, incrementBadgeCount, isSharedToGroup, isMountedRef, onAnimationComplete, scheduledWork])

  // Accessibility/perf: honor reduced motion by skipping the overlay animation.
  // Side-effects are performed in an effect to avoid running them during render.
  useEffect(() => {
    if (!prefersReducedMotion) return
    if (reducedMotionHandledRef.current) return
    reducedMotionHandledRef.current = true

    if (isSharedToGroup) incrementBadgeCount()
    onAnimationComplete?.()
  }, [prefersReducedMotion, incrementBadgeCount, isSharedToGroup, onAnimationComplete])

  const handlePointsAnimationComplete = useCallback(() => {
    // Cancel any pending work (e.g., if PointsAnimator completes twice).
    scheduledWork.clearAll()

    scheduledWork.scheduleTimeout(() => {
      if (!isMountedRef.current) return
      setPhase('revealing')

      scheduledWork.scheduleTimeout(() => {
        if (!isMountedRef.current) return
        startFlyAnimation()
      }, TIMING.CARD_ANIMATION_DELAY)
    }, TIMING.POST_COUNT_DELAY)
  }, [isMountedRef, scheduledWork, startFlyAnimation])

  // When reduced motion is preferred, skip rendering the overlay entirely.
  if (prefersReducedMotion) return null

  const isPointsFading = phase === 'revealing'
  const showPoints = phase === 'counting' || isPointsFading
  const isBackdropFading = phase !== 'counting'
  const flyDurationSeconds = duration / 1000
  const cardCss = getCardPhaseCss(flyDurationSeconds)[phase] ?? cardBaseCss

  return (
    <>
      <div css={getBackdropCss(isBackdropFading)} />
      <div css={overlayCss} data-testid="fly-to-feed-animation" data-animating={(phase === 'flying').toString()}>
        {showPoints && (
          <div css={getPointsContainerCss(isPointsFading)}>
            <PointsAnimator
              startPoints={startPoints}
              duration={TIMING.COUNT_UP}
              endPoints={endPoints}
              animationCompleteHandler={handlePointsAnimationComplete}
            />
            <MarginBox marginX="md" marginY="md">
              <H1 size="H2" centered>
                {t('finish.nicely-done', 'NICELY DONE!')}
              </H1>
              <Paragraph centered>{subtextMessage}</Paragraph>
            </MarginBox>
          </div>
        )}

        <div ref={cardRef} css={cardCss} style={cardStyle}>
          <StoryThumbnailCard
            doBleed={!!headerImage}
            headerImage={headerImage}
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            title={title}
            firstAnswerBody={firstAnswerBody}
          />
        </div>
      </div>
    </>
  )
}

FlyToFeedAnimation.propTypes = {
  duration: number,
  onAnimationComplete: func,
  startPoints: number,
  endPoints: number,
  isSharedToGroup: bool,
  headerImage: string,
  imageWidth: number,
  imageHeight: number,
  title: string,
  firstAnswerBody: string,
  cardPosition: shape({
    top: number,
    left: number,
    width: number,
    height: number,
  }),
}
